import { formatMarketplaceDate, MARKETPLACE_TIMEZONE } from '@/lib/amazon-date'

/**
 * #1029 Free kullanıcıya sınırlı erişim — site tarafı tek kaynak.
 *
 * Sunucu "limited" modundayken (`useSerpQuota().isLimited`) Pro olmayan kullanıcıya
 * KISMEN açılan sayfaların yolları. Hem `RequirePro` (sayfa girişi) hem Sidebar
 * (kilit simgesi) buradan okur; iki yerde kopya tutulmaz.
 *
 * Yalnız sunucunun Free'ye desteklediği yüzeyler listededir.
 * Liste DIŞI (kasıtlı): KeyInsight ve Listing Builder (karar: Free'ye hiç).
 * Açılış kapsamı (sunucu kodundan doğrulandı):
 *  - New Release: arama + ürün ucu, satış tahmini ürün başına günlük hak (ortak havuz);
 *  - Akademi: katalog/video/ilerleme uçlarının hepsi Free'ye sınırsız açık;
 *  - Amazon P&L: tarih kapısı (bugün-7 .. bugün); aralık dışı 403 `PLAN_REQUIRED/pl_range`.
 */
export const FREE_OPEN_PATHS: readonly string[] = [
  '/app/product-list', // Favoriler
  '/app/keyword-lists',
  '/app/rank-tracker',
  '/app/sales-estimator',
  '/app/deepsearch',
  '/app/freqfinder',
  '/app/profit-calculator',
  '/app/new-release', // satış tahmini ürün başına hak; satış filtresi/sıralaması Pro
  '/app/academy', // + video ayrıntısı (`/app/academy/:liste/:video`)
  '/app/amazon/dashboard', // P&L: yalnız son 7 gün
]

/** Yol Free'ye kısmen açık mı? (Alt yol / sorgu dizesi tolere edilir; tam eşleşme ya da `/` ile devam.) */
export function isFreeOpenPath(path: string | null | undefined): boolean {
  if (!path) return false
  const clean = path.split(/[?#]/)[0].replace(/\/+$/, '')
  return FREE_OPEN_PATHS.some((open) => clean === open || clean.startsWith(`${open}/`))
}

/** Anahtar kelime listesi sınırı dolunca satır içi/toast metni. */
/** Sınır dolu ve biliniyorken "Yeni Liste" düğmesinin kilit ipucu (form hiç açılmaz). */
export function freeKeywordListCreateLockedMessage(limit: number): string {
  return `Ücretsiz planda en fazla ${limit} liste oluşturabilirsiniz. Daha fazlası için Pro'ya geçin.`
}

export function freeKeywordListLimitMessage(limit: number | null | undefined): string {
  const count = typeof limit === 'number' && Number.isFinite(limit) ? limit : null
  return count === null
    ? "Ücretsiz planda anahtar kelime listesi sınırına ulaştınız. Daha fazlası için Pro'ya geçin."
    : `Ücretsiz planda en fazla ${count} anahtar kelime listesi oluşturabilirsiniz. Daha fazlası için Pro'ya geçin.`
}

const knownLimit = (limit: number | null | undefined): number | null =>
  typeof limit === 'number' && Number.isFinite(limit) ? limit : null

/** Favori sınırı dolunca toast metni. */
export function freeFavoriteLimitMessage(limit: number | null | undefined): string {
  const n = knownLimit(limit)
  return n === null
    ? "Ücretsiz planda favori sınırına ulaştınız. Daha fazlası için Pro'ya geçin."
    : `Ücretsiz planda en fazla ${n} favori ekleyebilirsiniz. Daha fazlası için Pro'ya geçin.`
}

/** Rank Tracker ürün sınırı dolunca mesaj. */
export function freeRankTrackerProductLimitMessage(limit: number | null | undefined): string {
  const n = knownLimit(limit)
  return n === null
    ? 'Ücretsiz planda ürün takip sınırına ulaştınız.'
    : `Ücretsiz planda en fazla ${n} ürün takip edebilirsiniz.`
}

/** Rank Tracker anahtar kelime sınırı dolunca mesaj (sınır ÜRÜN BAŞINA). */
export function freeRankTrackerKeywordLimitMessage(limit: number | null | undefined): string {
  const n = knownLimit(limit)
  return n === null
    ? 'Ücretsiz planda ürün başına anahtar kelime sınırına ulaştınız.'
    : `Ücretsiz planda ürün başına en fazla ${n} anahtar kelime takip edebilirsiniz.`
}

/** Sunucunun `feature` alanına göre Rank Tracker sınır mesajı; Rank Tracker dışı özellik → null. */
export function freeRankTrackerLimitMessage(
  feature: string | null | undefined,
  limit: number | null | undefined,
): string | null {
  if (feature === 'rank_tracker_products') return freeRankTrackerProductLimitMessage(limit)
  if (feature === 'rank_tracker_keywords') return freeRankTrackerKeywordLimitMessage(limit)
  return null
}

type MinimalKeywordLimit = { is_admin: boolean }

/**
 * Rank Tracker sayfa üstündeki TOPLAM kelime kullanımı ("3/20") göstergesi.
 * - Sunucu "limited" değil → girdi AYNEN (bit-bit eskisi gibi).
 * - Limited + admin → girdi aynen (sınırsız rozeti).
 * - Limited + Free → null: Free'de kelime sınırı ürün başınadır, toplam sayı yanlış olurdu.
 */
export function resolveKeywordTotalDisplay<T extends MinimalKeywordLimit>(keywordLimit: T | null, isLimited: boolean): T | null {
  if (!isLimited || !keywordLimit || keywordLimit.is_admin) return keywordLimit
  return null
}

/** Free ürün başına kelime sınırı: yalnız limited + sunucu bildiriyorsa sayı, aksi halde null (kilit/sayaç gizlenir). */
export function resolveFreeKeywordsPerProduct(
  isLimited: boolean,
  freeLimits: { rank_tracker_keywords_per_product?: number } | null,
): number | null {
  const max = freeLimits?.rank_tracker_keywords_per_product
  return isLimited && typeof max === 'number' ? max : null
}

/** Free ürün sınırı: yalnız limited + sınır biliniyorsa dolu döner, aksi halde null (satır gizlenir). */
export function resolveFreeProductLimit(
  current: number,
  isLimited: boolean,
  freeLimits: { rank_tracker_products?: number } | null,
): { current: number; max: number; remaining: number } | null {
  const max = freeLimits?.rank_tracker_products
  if (!isLimited || typeof max !== 'number') return null
  return { current, max, remaining: Math.max(max - current, 0) }
}

/** Ürün sınırı dolu ve biliniyorken "Kayıt Ekle" düğmesinin kilit ipucu (form hiç açılmaz). */
export function freeRankTrackerProductLockedMessage(limit: number): string {
  return `Ücretsiz planda en fazla ${limit} ürün takip edebilirsiniz. Daha fazlası için Pro'ya geçin.`
}

/** Üründe kelime hakkı dolu: "ekle" eyleminin kilit ipucu. */
export function freeRankTrackerKeywordLockedMessage(limit: number): string {
  return `Ücretsiz planda ürün başına en fazla ${limit} anahtar kelime takip edebilirsiniz. Daha fazlası için Pro'ya geçin.`
}

// ---------------------------------------------------------------------------
// Amazon P&L — Free "son 7 gün" penceresi (sunucu: services/amazon-service/dashboard_home/services/free_window.py)
// ---------------------------------------------------------------------------

/** Sunucudaki `LIMIT_DAYS`: izin penceresi [bugün-7, bugün] (Pasifik saati, iki uç dahil). */
export const FREE_PNL_LIMIT_DAYS = 7
/** Free'de seçilebilen hazır dönemler (hepsi pencere içinde: bugün, dün, dünde biten son 7 gün). */
export const FREE_PNL_PRESETS: readonly string[] = ['today', 'yesterday', 'last_7d']
/** Free dönem grubunun anahtarı (yalnız limited modda görünür; `PERIOD_GROUP_PRESETS`'e girmez). */
export const FREE_PNL_GROUP_KEY = 'free_7d'
export const FREE_PNL_RANGE_MESSAGE =
  "Ücretsiz planda son 7 günün verilerini görebilirsiniz. Daha geniş aralık için Pro'ya geçin."
/**
 * 403 bandı metni. Sunucu pencereyi (`details.allowed`) bildirdiyse sınırı da yazar
 * ("... görebilirsiniz (12 Eki – 19 Eki). ..."); bildirmediyse sade cümle.
 */
export function freePnlRangeMessage(allowed: FreePnlWindow | null | undefined): string {
  if (!allowed) return FREE_PNL_RANGE_MESSAGE
  const fmt = (ymd: string) => formatMarketplaceDate(`${ymd}T12:00:00Z`, { day: 'numeric', month: 'short' }, 'tr-TR')
  return `Ücretsiz planda son 7 günün verilerini görebilirsiniz (${fmt(allowed.start)} – ${fmt(allowed.end)}). Daha geniş aralık için Pro'ya geçin.`
}
export const FREE_PNL_PICKER_HINT = 'Ücretsiz planda yalnız son 7 gün seçilebilir.'
export const FREE_PNL_LOCKED_TIP = "Pro'ya özel"

export interface FreePnlWindow {
  /** 'YYYY-MM-DD' */
  start: string
  /** 'YYYY-MM-DD' */
  end: string
}

/** Pasifik saatine göre bugün ('YYYY-MM-DD'); tarayıcı saat dilimi etkilemez. */
export function ptTodayString(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: MARKETPLACE_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now)
}

function addDays(ymd: string, days: number): string {
  const d = new Date(`${ymd}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

export function freePnlWindow(now: Date = new Date()): FreePnlWindow {
  const today = ptTodayString(now)
  return { start: addDays(today, -FREE_PNL_LIMIT_DAYS), end: today }
}

/** Aralık pencerenin içinde mi (ters sıralı aralık dışarıda sayılır; sunucuyla aynı). */
export function isInsideFreePnlWindow(start: string, end: string, window: FreePnlWindow): boolean {
  return window.start <= start && start <= end && end <= window.end
}

/** Free'de seçilebilen tek-dönem seçimi mi? (hazır dönem ya da pencere içi özel aralık) */
export function isFreePnlSinglePeriod(
  period: { preset?: string; startDate?: string; endDate?: string },
  window: FreePnlWindow,
): boolean {
  if (period.preset) return FREE_PNL_PRESETS.includes(period.preset)
  if (period.startDate && period.endDate) return isInsideFreePnlWindow(period.startDate, period.endDate, window)
  return false
}

export interface PlRangeBlock {
  allowed: FreePnlWindow | null
  reason: 'range' | 'compare' | null
}

/**
 * 403 gövdesi P&L tarih kapısı mı? Gövde: `{ success:false, error:{ code:'PLAN_REQUIRED',
 * details:{ feature:'pl_range', limit_days, allowed:{start,end}, reason } } }`. Başka hata → null.
 */
export function readPlRangeBlock(err: unknown): PlRangeBlock | null {
  if (!err || typeof err !== 'object') return null
  const { status, body } = err as { status?: unknown; body?: unknown }
  if (status !== 403 || !body || typeof body !== 'object') return null
  const error = (body as { error?: unknown }).error
  if (!error || typeof error !== 'object') return null
  const { code, details } = error as { code?: unknown; details?: unknown }
  if (code !== 'PLAN_REQUIRED' || !details || typeof details !== 'object') return null
  const d = details as { feature?: unknown; allowed?: unknown; reason?: unknown }
  if (d.feature !== 'pl_range') return null
  let allowed: FreePnlWindow | null = null
  if (d.allowed && typeof d.allowed === 'object') {
    const { start, end } = d.allowed as { start?: unknown; end?: unknown }
    if (typeof start === 'string' && typeof end === 'string') allowed = { start, end }
  }
  return { allowed, reason: d.reason === 'compare' ? 'compare' : d.reason === 'range' ? 'range' : null }
}

// ---------------------------------------------------------------------------
// New Release — satış tahmini hakkı (sunucu: services/new-releases-service/utils/free_quota.py)
// ---------------------------------------------------------------------------

export const NR_SALES_FILTER_FEATURE = 'new_releases_sales_filter'
export const NR_SALES_FILTER_MESSAGE =
  "Satış filtresi ve satışa göre sıralama Pro'ya özel. Diğer filtreleri ücretsiz kullanabilirsiniz."
export const NR_QUOTA_UNAVAILABLE_MESSAGE = 'Şu an hak bilgisi alınamadı, biraz sonra tekrar deneyin.'
export const NR_SALES_LOCKED_TIP = "Satış filtresi Pro'ya özel"

function fastApiDetail(err: unknown): { code?: unknown; error?: unknown; details?: unknown } | null {
  if (!err || typeof err !== 'object') return null
  const body = (err as { body?: unknown }).body
  if (!body || typeof body !== 'object') return null
  const detail = (body as { detail?: unknown }).detail
  return detail && typeof detail === 'object' ? (detail as { code?: unknown; error?: unknown; details?: unknown }) : null
}

/** 403 `{detail:{code:'PLAN_REQUIRED', details:{feature:'new_releases_sales_filter'}}}` mi? */
export function isNewReleasesSalesFilterBlock(err: unknown): boolean {
  if ((err as { status?: unknown } | null)?.status !== 403) return false
  const detail = fastApiDetail(err)
  if (!detail || (detail.code !== 'PLAN_REQUIRED' && detail.error !== 'PLAN_REQUIRED')) return false
  const feature = (detail.details as { feature?: unknown } | undefined)?.feature
  return feature === NR_SALES_FILTER_FEATURE
}

/** 503 `{detail:{code:'quota_unavailable'}}` mi? (havuz okunamadı) */
export function isNewReleasesQuotaUnavailable(err: unknown): boolean {
  if ((err as { status?: unknown } | null)?.status !== 503) return false
  const detail = fastApiDetail(err)
  return detail?.code === 'quota_unavailable' || detail?.error === 'quota_unavailable'
}
